import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Typography,
  styled,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  PushPin,
  PushPinOutlined,
  CorporateFare,
  Domain,
  GroupWork,
  Person,
  LocationOn,
  Schedule,
  Logout,
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const DRAWER_WIDTH = 200;
const COLLAPSED_WIDTH = 56;

// Базовая тема (для контента); сам Drawer стилизуем отдельно, чтобы не ломать светлый контент
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#ff4081' },
    background: { default: '#f5f7fa', paper: '#ffffff' },
    text: { primary: '#1a1a1a', secondary: '#4a4a4a' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    h6: { fontWeight: 700, fontSize: '0.9rem', color: 'rgb(64 64 64)' },
    body2: { fontSize: '0.7rem', fontWeight: 500 },
  },
});

// «шапка» сайдбара с глянцевым градиентом
const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 1.25),
  // Слоёный градиент: основной #a15c40, в центре блик #faceb7
  backgroundImage: `
    linear-gradient(135deg, #a15c40 0%, #a15c40 38%, #faceb7 44%, #a15c40 50%, #8f5037 100%),
    linear-gradient(180deg, rgba(255,255,255,0.15), rgba(0,0,0,0.1))
  `,
  color: '#ffffff',
  borderRadius: '0 12px 0 0',
  boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
}));

const LogoText = styled(Typography)({
  fontWeight: 800,
  letterSpacing: '0.4px',
  fontSize: '0.96rem',
  textTransform: 'uppercase',
  color: '#fff',
  textShadow: '0 1px 0 rgba(0,0,0,0.35)',
  whiteSpace: 'nowrap',
});

const SidebarDivider = styled(Divider)({
  borderColor: 'rgba(255,255,255,0.08)',
});

const LogoutButton = styled(ListItemButton)(({ theme }) => ({
  margin: theme.spacing(0.75),
  borderRadius: 6,
  color: '#ffb4a9',
  padding: '6px 9px',
  transition: 'all 0.25s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 77, 64, 0.12)',
    transform: 'translateX(4px)',
    color: '#ffc0b8',
  },
  '& .MuiListItemIcon-root': {
    color: '#ffb4a9',
  },
}));

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState((localStorage.getItem('userRole') || '').toLowerCase());
  const [pinned, setPinned] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('sidebarPinned')) || false;
    } catch {
      return false;
    }
  });
  const [open, setOpen] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('sidebarOpen'));
      return stored != null ? stored : false;
    } catch {
      return false;
    }
  });

  const getAuthToken = () => localStorage.getItem('auth_token') || '';

  const fetchUserRole = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/admin/user/role?auth_token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (data.status === 'success' && data.role) {
        const newRole = data.role.toLowerCase();
        if (newRole !== userRole) {
          setUserRole(newRole);
          localStorage.setItem('userRole', newRole);
        }
      } else if (data.status === 'error') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        navigate('/login');
      }
    } catch (err) {
      console.error('Error fetching user role:', err);
    }
  }, [userRole, navigate]);

  useEffect(() => {
    fetchUserRole();
  }, [fetchUserRole]);

  useEffect(() => {
    localStorage.setItem('sidebarPinned', JSON.stringify(pinned));
    if (!pinned) {
      setOpen(false);
      localStorage.setItem('sidebarOpen', JSON.stringify(false));
    }
  }, [pinned]);

  useEffect(() => {
    if (pinned) {
      localStorage.setItem('sidebarOpen', JSON.stringify(open));
    }
  }, [open, pinned]);

  const handlePinToggle = () => setPinned(prev => !prev);
  const handleExpandToggle = () => { if (pinned) setOpen(prev => !prev); };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    setUserRole('');
    navigate('/admin');
  };

  const menuItems = [
    { text: 'Organizations', icon: <CorporateFare fontSize="small" />, path: '/admin/org_list', allow: ['admin', 'ceo', 'hr'].includes(userRole) },
    { text: 'Departments', icon: <Domain fontSize="small" />, path: '/admin/dept_list', allow: ['admin', 'ceo', 'hr', 'hod', 'supervisor'].includes(userRole) },
    { text: 'Teams', icon: <GroupWork fontSize="small" />, path: '/admin/team_list', allow: ['admin', 'ceo', 'hr', 'hod', 'manager', 'supervisor'].includes(userRole) },
    { text: 'Users', icon: <Person fontSize="small" />, path: '/admin/user_management', allow: ['admin', 'ceo'].includes(userRole) },
    { text: 'Locations', icon: <LocationOn fontSize="small" />, path: '/admin/allowed_locations', allow: ['admin', 'ceo', 'hr', 'hod', 'manager', 'supervisor'].includes(userRole) },
    { text: 'Schedules', icon: <Schedule fontSize="small" />, path: '/admin/schedule_list', allow: userRole === 'admin' },
  ].filter((item) => item.allow);

  const drawerWidth = open ? DRAWER_WIDTH : COLLAPSED_WIDTH;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex' }}>
        <Drawer
          variant="permanent"
          open={open}
          onMouseEnter={() => { if (!pinned) setOpen(true); }}
          onMouseLeave={() => { if (!pinned) setOpen(false); }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              transition: (t) =>
                t.transitions.create('width', {
                  easing: t.transitions.easing.easeInOut,
                  duration: t.transitions.duration.standard,
                }),
              // === Тёмная палитра для сайдбара ===
              backgroundColor: '#2e3230',
              borderRight: 'none',
              boxShadow: '0 6px 16px rgba(0,0,0,0.35)',
              borderRadius: '0 12px 12px 0',
              color: '#e9ecea',
            },
          }}
        >
          <HeaderBox>
            {open && <LogoText>AX Attendace</LogoText>}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}>
                <span>
                  <IconButton
                    onClick={handlePinToggle}
                    size="small"
                    sx={{ color: '#ffffff', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                  >
                    {pinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={open ? 'Collapse' : 'Expand'}>
                <span>
                  <IconButton
                    onClick={handleExpandToggle}
                    size="small"
                    disabled={!pinned}
                    sx={{ color: '#ffffff', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                  >
                    {open ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </HeaderBox>

          <SidebarDivider />

          <List
            sx={{
              px: 0.5,
              '& .MuiListItemButton-root': {
                borderRadius: 6,
                margin: '4px 6px',
                padding: '8px 10px',
                transition: 'all 0.2s ease',
                color: '#e9ecea',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  transform: 'translateX(4px)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(250, 206, 183, 0.18)', // оттенок от #faceb7
                  color: '#ffffff',
                  '& .MuiListItemIcon-root': { color: '#ffffff' },
                  '&:hover': {
                    backgroundColor: 'rgba(250, 206, 183, 0.25)',
                  },
                },
              },
              '& .MuiListItemIcon-root': {
                color: '#d8c4bb', // иконки на тёмном фоне
                minWidth: 34,
              },
              '& .MuiListItemText-primary': {
                fontSize: '0.78rem',
                letterSpacing: 0.2,
              },
            }}
          >
            {menuItems.map(({ text, icon, path }) => (
              <Tooltip key={text} title={!open ? text : ''} placement="right">
                <ListItemButton
                  onClick={() => navigate(path)}
                  selected={location.pathname === path}
                >
                  <ListItemIcon>{icon}</ListItemIcon>
                  {open && <ListItemText primary={text} />}
                </ListItemButton>
              </Tooltip>
            ))}
          </List>

          <SidebarDivider />

          <Box sx={{ mt: 'auto', mb: 0.75 }}>
            <Tooltip title={!open ? 'Logout' : ''} placement="right">
              <LogoutButton onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                {open && <ListItemText primary="Logout" />}
              </LogoutButton>
            </Tooltip>
          </Box>
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 2,
            ml: `${drawerWidth}px`,
            transition: (t) =>
              t.transitions.create(['margin'], {
                easing: t.transitions.easing.easeInOut,
                duration: t.transitions.duration.standard,
              }),
          }}
        >
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
